/*
 * Splide autoplay without a per-frame loop.
 *
 * Splide 4 times autoplay with a requestAnimationFrame loop that runs on every
 * screen refresh for as long as a slider autoplays, even off screen or inside a
 * closed drawer. Each tick makes the browser build a new frame for the whole
 * page, which keeps the main thread busy on low-end phones.
 *
 * This swaps in an Autoplay component that waits with one timeout per slide.
 * The options and API stay the same: play(), pause(), isPaused(),
 * pauseOnHover, pauseOnFocus, resetProgress, data-splide-interval, the toggle
 * button and the autoplay:play / autoplay:pause events. The countdown also
 * waits while the slider is off screen or the tab is hidden. Sliders that render
 * Splide's own progress bar keep the original component.
 */
(() => {
  const Splide = window.Splide;
  if (!Splide || Splide.prototype.mount.timerAutoplay) return;

  function TimerAutoplay(splide, Components, options) {
    const { root, toggle, track } = Components.Elements;
    const cleanups = [];
    let interval = options.interval;
    let remaining = interval;
    let startedAt = 0;
    let timer = 0;
    let paused = true;
    let stopped = options.autoplay === 'pause';
    let hovered = false;
    let focused = false;
    let inView = !window.IntersectionObserver;
    let watching = false;
    let observer = null;

    function listen(target, types, handler) {
      types.split(' ').forEach((type) => {
        target.addEventListener(type, handler);
        cleanups.push(() => target.removeEventListener(type, handler));
      });
    }

    function halt() {
      if (!timer) return;
      clearTimeout(timer);
      timer = 0;
      remaining = Math.max(0, remaining - (Date.now() - startedAt));
    }

    function arm() {
      if (timer || paused || !inView || document.hidden) return;
      startedAt = Date.now();
      timer = setTimeout(tick, remaining);
    }

    function tick() {
      timer = 0;
      remaining = interval;
      splide.emit('autoplay:playing', 1);
      splide.go('>');
      arm();
    }

    function rewind() {
      clearTimeout(timer);
      timer = 0;
      remaining = interval;
      splide.emit('autoplay:playing', 0);
      arm();
    }

    function watch() {
      if (watching) return;
      watching = true;
      listen(document, 'visibilitychange', () => (document.hidden ? halt() : arm()));
      if (!window.IntersectionObserver) return;
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          inView = entry.isIntersecting && entry.intersectionRatio > 0;
          inView ? arm() : halt();
        },
        { threshold: [0, 0.01] }
      );
      observer.observe(root);
    }

    function update() {
      if (!toggle) return;
      toggle.classList.toggle('is-active', !stopped);
      toggle.setAttribute('aria-label', options.i18n[stopped ? 'play' : 'pause']);
    }

    function play() {
      if (!paused || !Components.Slides.isEnough()) return;
      if (options.resetProgress !== false || !remaining) remaining = interval;
      paused = false;
      hovered = focused = stopped = false;
      update();
      watch();
      arm();
      splide.emit('autoplay:play');
    }

    function pause(stop) {
      stopped = stop === undefined ? true : !!stop;
      update();
      if (paused) return;
      halt();
      paused = true;
      splide.emit('autoplay:pause');
    }

    function autoToggle() {
      if (stopped) return;
      hovered || focused ? pause(false) : play();
    }

    function onMove(index) {
      const slide = Components.Slides.getAt(index);
      interval = (slide && +slide.slide.getAttribute('data-splide-interval')) || options.interval;
      rewind();
    }

    return {
      mount() {
        if (!options.autoplay) return;
        if (options.pauseOnHover) {
          listen(root, 'mouseenter mouseleave', (event) => {
            hovered = event.type === 'mouseenter';
            autoToggle();
          });
        }
        if (options.pauseOnFocus) {
          listen(root, 'focusin focusout', (event) => {
            focused = event.type === 'focusin';
            autoToggle();
          });
        }
        if (toggle) {
          listen(toggle, 'click', () => (stopped ? play() : pause(true)));
          toggle.setAttribute('aria-controls', track.id);
        }
        splide.on('move', onMove);
        splide.on('scroll refresh', rewind);
        if (!stopped) play();
        update();
      },
      destroy() {
        clearTimeout(timer);
        timer = 0;
        paused = true;
        watching = false;
        if (observer) observer.disconnect();
        observer = null;
        cleanups.splice(0).forEach((cleanup) => cleanup());
      },
      play,
      pause,
      isPaused: () => paused,
    };
  }

  const mount = Splide.prototype.mount;
  Splide.prototype.mount = function (Extensions, Transition) {
    if (this.root && this.root.querySelector('.splide__progress__bar')) {
      return mount.call(this, Extensions, Transition);
    }
    if (Extensions || !this.timerExtensions) {
      this.timerExtensions = Object.assign({ Autoplay: TimerAutoplay }, Extensions);
    }
    return mount.call(this, this.timerExtensions, Transition);
  };
  Splide.prototype.mount.timerAutoplay = true;
})();
